import {ComponentFactoryResolver, Injectable, Injector} from '@angular/core';
import {PiwikScriptComponent} from './piwik-script.component';
import {IPiwikConfig, piwikConfig} from './piwik.config';
import {environment} from '@environments/environment';

declare const _mtm;

export enum EVENT_KATEGORIE {
  'FEHLER'= 'error',
  'PRIMARY_BUTTON'= 'Primary Button',
  'CHECKBOX' = 'Checkbox'
}

export enum ACTIONS_TYPES  {
  'CLICK' = 'klick',
  'FORM' = 'FORM'

}

const COOKIE_INDEX = 2;
/**
 * Ausnahmsweise nutzen wir hier NICHT "@Injectable({providedIn: 'root'})".
 *
 * Stattdessen wird die Instanz des Services als Provider im piwikModule registriert,
 * da wir dort mit dem "APP_INITIALIZER" die Metehode "PiwikService.init()"
 * beim Bootstrapen der App ausführen können.
 *
 * siehe: https://angular.io/guide/architecture-services
 */
@Injectable()
export class PiwikService {

  private readonly _config: IPiwikConfig = piwikConfig;

  static getCookie(name) {
    const v = document.cookie.match( `(^|;) ?${name}=([^;]*)(;|$)`);
    return v ? v[COOKIE_INDEX] : null;
  }

  constructor(
    private readonly _componentFactoryResolver: ComponentFactoryResolver,
    private readonly _injector: Injector
  ) {
  }

  /**
   * Injected das PIWIK Script ans Ende des <body>, wenn
   * - "cookie_consent" nicht "denied" ist (Datenschutz Cookie)
   * - "trackingActivated" = true ist  (environment.ts)
   */
  init(): void {
    if (this._isTrackingAllowed()) {
      this._appendPiwikScriptComponent();
    }
  }

  /**
   * Page views werden getracked
   * @param title
   * @param url
   */
  trackPageVisit(seitenTitel: string = '') {
    if (this._isTrackingAllowed()) {
      _mtm.push({
        'seite': {
          'seitenBereich': 'WBA',
          'seitenVersion': '',
          'seitenUxDesignTyp': '',
          'seitenZielgruppe': 'ALGII_WBA',
          'seitenCanonicalURL': document.location.host + document.location.pathname,
          'seitenStatusCode': '',
          'seitenPublikationsdatum': '',
          'seitenTitel': seitenTitel,
          'seitenCMS': '',
          'seitenZufriedenheit': '',
          'seitenKomponente': '',
          'seitenKomponenteVersion': '',
        },
        'event': 'pageLoad',
      });
    }
  }
  /**
   *
   * trackEvent-Tracking. Tracking von Clicks etc.
   *
   * @param category
   * @param name
   * @param action
   * @param value
   */
  trackEventAction(category: EVENT_KATEGORIE, action: ACTIONS_TYPES, name: string, value = '') {
    if (this._isTrackingAllowed()) {
      _mtm.push({
        'seite': {
          'eventCategory': category,
          'eventAction': action,
          'eventName': name,
          'eventValue': value,
          'eventFachlich': '',
        },
        'event': 'trackEvent',
      });
    }
  }
  /**
   * Return true, wenn tracking aktiv und Cookie "cookie_consent" NICHT den Wert "denied" hat.
   * - Prüft ob Tracking aktiviert ist (siehe environment.ts)
   * - Prüft "cookie_consent" !== "denied" (das Datenschutz Disclaimer Cookie)
   * @private
   */
  private _isTrackingAllowed(): boolean {
    if (!this._config.trackingActivated || String(environment.piwik.activated) !== 'true') {
      return false;
    }
    return this._isTrackingAllowed_byCookie();
  }

  /**
   * Prüft ob das Tracking evtl. durch das Cookie "cookie_consent" NICHT erlaubt ist.
   *
   * Wann ist es nicht erlaubt (Stand 02.2019):
   * - wenn das Cookie "cookie_consent" den Wert "denied" hat
   *
   * Hinweis:
   * Momentan ist das BA-Tracking nach dem Verfahren "OPT OUT" umgesetzt.
   * Das bedeutet, dass der Benutzer auf der Datenschutz-Seite aktiv widersprechen muss (setzt cookie_consent auf "denied"), ...
   * ... um das Tracking zu unterbinden!
   */
  private _isTrackingAllowed_byCookie(): boolean {
    const cookieValue: string = PiwikService.getCookie(this._config.disclaimerCookie.name);
    // -- Nicht erlaubt, wenn Cookie den Wert "denied" hat
    return cookieValue !== this._config.disclaimerCookie.denied;
  }

  /**
   * Instanziiert dynamisch die Piwik-Script Component.
   * Die Piwik-Script Componente injektet das PIWIK Script ans Ende des Body Tags
   * @private
   */
  private _appendPiwikScriptComponent(): void {
    const componentFactory = this._componentFactoryResolver.resolveComponentFactory(PiwikScriptComponent);
    componentFactory.create(this._injector);
  }
}
